import React, { useRef } from "react";
import PropTypes from "prop-types";
import Actions from "actions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Picture } from "components/UI";
import { useMutation } from "react-query";
import api from "api";
import { Field, Form, Formik, useFormikContext } from "formik";
import { TextInput } from "components/Form";
import notify from "utils/notification";
import { nameFormat } from "utils/format";
import { blockUserIcon } from "assets";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

const reasonsList = [
  "Spam",
  "Impersonation",
  "Violence",
  "Harassment or hateful speech",
  "Adult content",
  "Defamation",
  "Others",
];

const ReasonRadioGroup = () => {
  const { values, setFieldValue } = useFormikContext();
  return (
    <div className="flex flex-col gap-2">
      {reasonsList.map((reason) => (
        <button
          key={reason}
          type="button"
          className={`flex items-center w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors ${
            values.reason === reason ? "border-easter bg-white" : "border-gray-200 bg-gray-50"
          }`}
          onClick={() => setFieldValue("reason", reason)}
        >
          {values.reason === reason ? (
            <RadioButtonCheckedIcon fontSize="small" className="text-easter flex-shrink-0" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" className="text-gray-400 flex-shrink-0" />
          )}
          <span className="ml-3 text-sm text-left">{reason}</span>
        </button>
      ))}
    </div>
  );
};

const BlockAndReportUserForm = ({ reportedUser }) => {
  const { id, first_name, last_name } = reportedUser;
  const userName = nameFormat({ firstName: first_name, lastName: last_name });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resetFormRef = useRef(null);

  const { mutate: reportAndBlockMutate } = useMutation((formData) => api.blockUser(id, formData), {
    onSuccess: () => {
      resetFormRef.current?.();
      notify("Successfully reported and blocked user");
      dispatch(Actions.hideDialog());
      navigate("/network");
    },
    onError: (e) => {
      notify(e?.response?.data?.error || "Error reporting and blocking user", null, "danger");
    },
  });

  const handleSubmit = ({ reason, other_reason }) => {
    const resolvedReason = reason === "Others" && other_reason ? other_reason : reason;
    reportAndBlockMutate({ id, reason: resolvedReason });
  };

  return (
    <div className="flex flex-col text-left text-base" style={{ maxHeight: "60vh" }}>
      {/* Fixed header */}
      <div className="flex flex-col gap-2 px-1 pb-3">
        <div className="flex items-center gap-2">
          <Picture src={blockUserIcon} className="w-6" />
          <h2 className="font-semibold text-lg">Report and Block {userName}?</h2>
        </div>
        <p className="text-sm text-gray-600">
          Help us understand the reason you would like to report{" "}
          <span className="font-semibold">{userName}</span>
        </p>
      </div>

      <Formik
        initialValues={{ reason: "", other_reason: "" }}
        validate={({ reason }) => {
          const errors = {};
          if (!reason) errors.reason = "Please select a reason";
          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, resetForm }) => {
          resetFormRef.current = resetForm;
          return (
            <Form className="flex flex-col min-h-0 flex-1">
              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 flex flex-col gap-4 pr-1">
                <ReasonRadioGroup />
                {touched.reason && errors.reason && (
                  <p className="text-xs text-red-500">{errors.reason}</p>
                )}

                {values.reason === "Others" && (
                  <Field
                    label="Please specify your reason"
                    placeholder="Please specify your reason"
                    name="other_reason"
                    type="text"
                    component={TextInput}
                  />
                )}

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 text-sm text-gray-700">
                  <p className="font-medium mb-2">
                    Reporting {userName} will block them from your network. They will no longer be
                    able to:
                  </p>
                  <ul className="space-y-1">
                    <li>&middot; Find you on the network</li>
                    <li>&middot; Message you</li>
                    <li>&middot; See events created by you</li>
                  </ul>
                </div>
              </div>

              {/* Fixed footer buttons */}
              <div className="flex flex-col justify-center items-center gap-3 pt-4">
                <Button
                  label="YES, BLOCK USER"
                  className="w-full sm:w-3/4 h-12 px-4"
                  type="submit"
                  isPill
                  theme="valencia"
                />
                <Button
                  label="NO, CANCEL"
                  className="w-full sm:w-3/4 h-12 px-4"
                  theme="plain"
                  outline
                  isPill
                  onClick={() => {
                    resetForm();
                    dispatch(Actions.hideDialog());
                  }}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

BlockAndReportUserForm.propTypes = {
  reportedUser: PropTypes.object.isRequired,
};

export default BlockAndReportUserForm;
